<script setup>
import ImportBacklinksAction from '@frontJs/components/actions/projectManagement/seo/ImportBacklinksAction.vue';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import UploadSimpleIcon from '@iconify-vue/ph/upload-simple';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastSuccess } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, websiteVariants, seoBacklinkPlatforms } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
    seoBacklinkPlatforms: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    exportData,
    selectionIds,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'website_variants.name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Site',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.websiteVariant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'platform',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Plateforme',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.platform.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sourceUrl',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'URL source',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: new URL(props.row.original.sourceUrl).hostname,
                    href: props.row.original.sourceUrl,
                    maxWidth: 250,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'date',
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date',
                }),
            cell: (props) =>
                props.row.original.date
                    ? h(TextCell, {
                          text: props.row.original.date,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'active',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actif',
                }),
            cell: (props) =>
                props.row.original.active !== null
                    ? h(StatusCell, {
                          status: props.row.original.active,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'targetUrl',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'URL cible',
                }),
            cell: (props) =>
                props.row.original.targetUrl !== null
                    ? h(LinkCell, {
                          text: props.row.original.targetUrl,
                          href: props.row.original.targetUrl,
                          maxWidth: 250,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'sourceAnchor',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Ancre source',
                }),
            cell: (props) =>
                props.row.original.sourceAnchor !== null
                    ? h(TextCell, {
                          text: props.row.original.sourceAnchor,
                          title: props.row.original.sourceAnchor,
                          maxWidth: 250,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'noFollow',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'No Follow',
                }),
            cell: (props) =>
                props.row.original.noFollow !== null
                    ? h(StatusCell, {
                          status: !props.row.original.noFollow,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'indirectLink',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Lien indirect',
                }),
            cell: (props) =>
                props.row.original.indirectLink !== null
                    ? h(StatusCell, {
                          status: props.row.original.indirectLink,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'checkedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Vérifié le',
                }),
            cell: (props) =>
                props.row.original.checkedAt
                    ? h(TextCell, {
                          text: props.row.original.checkedAt,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.seo.backlink',
                                    {
                                        tenant: tenant.id,
                                        id: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.seo.postBacklinks', {
        tenant: tenant.id,
    }),
    exportUrl: route('front.projectManagement.seo.postExportBacklinks', {
        tenant: tenant.id,
    }),
    selectionIdKey: 'id',
    columnPinning: {
        right: ['actions'],
    },
});

function importSeoBacklinksCallback() {
    toastSuccess('Importé avec succès');

    updateData();
}
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />

            <ImportBacklinksAction
                :tenant="tenant"
                :website-variants="websiteVariants"
                :seo-backlink-platforms="seoBacklinkPlatforms"
                :callback="importSeoBacklinksCallback"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="UploadSimpleIcon"
                        type="primary"
                    >
                        Importer
                    </ActionButton>
                </template>
            </ImportBacklinksAction>

            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.seo.backlink', {
                        tenant: tenant,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
